import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { LogoMark } from "@/components/Logo";

const RINGS = [0.45, 0.65, 0.85, 1.05];
// orbiting blips: [radiusFraction, startAngleDeg, duration, color]
const BLIPS = [
  [0.55, 20, 9, "#2DD4BF"],
  [0.75, 140, 13, "#06B6D4"],
  [0.95, 250, 17, "#8B5CF6"],
  [0.65, 300, 11, "#F59E0B"],
];

export default function WelcomeRadar() {
  const { t } = useTranslation();
  const [thinking, setThinking] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setThinking((v) => !v), 3200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative w-[300px] h-[300px] sm:w-[420px] sm:h-[420px] flex items-center justify-center" data-testid="welcome-radar">
      {/* soft glow */}
      <div className="absolute inset-0 rounded-full wo-glow-radial" />

      {/* concentric rings */}
      {RINGS.map((r, i) => (
        <motion.div key={i}
          className="absolute rounded-full border border-cyan-400/15"
          style={{ width: `${r * 100}%`, height: `${r * 100}%` }}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.12, duration: 0.6 }}
        />
      ))}

      {/* rotating sweep */}
      <div className="absolute rounded-full overflow-hidden" style={{ width: "105%", height: "105%" }}>
        <div className="radar-sweep" style={{ animationDuration: "5s" }} />
      </div>

      {/* pulsing outer ring */}
      <motion.div
        className="absolute rounded-full border border-teal-400/25"
        style={{ width: "105%", height: "105%" }}
        animate={{ scale: [1, 1.06, 1], opacity: [0.5, 0.15, 0.5] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* orbiting blips */}
      {BLIPS.map(([rf, angle, dur, color], i) => (
        <motion.div key={i} className="absolute" style={{ width: `${rf * 100}%`, height: `${rf * 100}%` }}
          animate={{ rotate: [angle, angle + 360] }}
          transition={{ duration: dur, repeat: Infinity, ease: "linear" }}>
          <span className="absolute left-1/2 -top-1 -translate-x-1/2 w-2.5 h-2.5 rounded-full blip"
            style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
        </motion.div>
      ))}

      {/* center: alternate logo <-> thinking shimmer */}
      <div className="relative z-10 flex flex-col items-center min-h-[92px] justify-center">
        <AnimatePresence mode="wait">
          {!thinking ? (
            <motion.div key="logo"
              initial={{ opacity: 0, scale: 0.92, filter: "blur(6px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.96, filter: "blur(6px)" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="flex flex-col items-center">
              <LogoMark size="text-4xl sm:text-5xl" asLink={false} />
              <div className="mt-3 text-[10px] sm:text-xs font-mono tracking-[0.3em] text-slate-400 uppercase text-center">
                {t("splash.tagline")}
              </div>
            </motion.div>
          ) : (
            <motion.div key="thinking"
              initial={{ opacity: 0, scale: 0.92, filter: "blur(6px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.96, filter: "blur(6px)" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="flex flex-col items-center">
              {/* Emergent-style pulsing ball */}
              <div className="relative flex items-center justify-center h-16 w-16">
                <motion.span
                  className="absolute rounded-full border border-teal-400/40"
                  style={{ width: "100%", height: "100%" }}
                  animate={{ scale: [1, 1.9], opacity: [0.6, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }} />
                <motion.span
                  className="absolute rounded-full border border-cyan-400/30"
                  style={{ width: "100%", height: "100%" }}
                  animate={{ scale: [1, 1.9], opacity: [0.5, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut", delay: 0.6 }} />
                <motion.span
                  className="rounded-full"
                  style={{ width: 34, height: 34, background: "radial-gradient(circle at 35% 30%, #99F6E4, #2DD4BF 45%, #06B6D4 100%)", boxShadow: "0 0 24px rgba(45,212,191,0.75), 0 0 48px rgba(6,182,212,0.4)" }}
                  animate={{ scale: [1, 1.18, 1], opacity: [0.9, 1, 0.9] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />
              </div>
              <span className="thinking-text font-display font-semibold text-lg sm:text-xl tracking-tight mt-4">
                {t("radar.scanning")}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
